// FROM: https://github.com/modusign/modusign-client/blob/b6c3d577fdd8384ea2f73c5ab605ead1afc782d1/app/src/components/ParticipantFormMeansField/index.tsx

import { faUserShield } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Text, Tooltip, ValidationMessage } from '@modusign/design-system';
import { useHosts } from '@modusign/react-signing';
import { SignerToReference, SigningMethod } from '@modusign/react-signing/types';
import cx from 'classnames';
import { produce } from 'immer';
import debounce from 'lodash/debounce';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';
import { v4 } from 'uuid';

import EmailAutoCompleteInput from '@app/components/EmailAutoCompleteInput';
import SignerLanguageSelect from '@app/components/SignerLanguageSelect';
import { CONTACT_INFO_MAX_LENGTH } from '@app/constants/contactInfoMaxLength';
import { AddressBookContact } from '@app/features/addressBook';
import InPersonContactInfoForm from '@app/features/apply-react-signing/components/SignerCardUI/InPersonContactInfoForm';
import SignerCardButtons from '@app/features/apply-react-signing/components/SignerCardUI/SignerCardButtons';
import SignerFormPhoneField from '@app/features/apply-react-signing/components/SignerCardUI/SignerFormPhoneField';
import { duplicatedContactInfoValidator } from '@app/features/apply-react-signing/libs/validations/checkDuplicatedContactInfo';
import { createDataLabel } from '@app/features/documents/utils/createDataLabel';
import { InPersonUpSellingModal, useShouldBlockInPersonSigning } from '@app/features/inPerson';
import pushAdobeDataLayer from '@app/libs/analytics/adobe';
import { validateEmail, validatePhoneNumber } from '@app/libs/validation';
import LocaleCode from '@app/store/LocaleCode';
import { getEmail, getName } from '@app/store/user/user/selectors';

import SigningMethodSelect from './SigningMethodSelect';

function getContact(method: SigningMethod, addressBookContact?: AddressBookContact | null) {
  if (
    (method === 'EMAIL' ||
      (method === 'IN_PERSON' && addressBookContact?.defaultForwardingMethod === 'EMAIL')) &&
    addressBookContact?.email
  ) {
    return addressBookContact.email;
  }

  if (
    (method === 'KAKAO' ||
      (method === 'IN_PERSON' && addressBookContact?.defaultForwardingMethod === 'KAKAO')) &&
    addressBookContact?.phoneNumber
  ) {
    return addressBookContact.phoneNumber;
  }

  return '';
}

interface SignerFormContactAndMethodFieldProps {
  addressBookContact?: AddressBookContact | null;
  isLocaleChangeAvailable: boolean;
  signerId: string;
  order: number;
  required?: boolean;
  type: SigningMethod | null;
  shouldSetUserAsDefaultHost?: boolean;
  selectableMethods: SigningMethod[];
  onSigningMethodChange: (value: SigningMethod | null) => void;
}

const defaultProps = {
  isLocaleChangeAvailable: false,
  locale: LocaleCode.KOREAN,
};

export default function SignerFormContactAndMethodField({
  addressBookContact,
  isLocaleChangeAvailable,
  signerId,
  order,
  required = true,
  type,
  shouldSetUserAsDefaultHost,
  selectableMethods,
  onSigningMethodChange,
}: SignerFormContactAndMethodFieldProps) {
  const { shouldBlockInPersonSigning } = useShouldBlockInPersonSigning();
  const [isInPersonUpSellModalShow, setIsInPersonUpSellModalShow] = useToggle(false);
  const { control, errors, clearErrors, getValues, reset, trigger } =
    useFormContext<Record<string, SignerToReference>>();

  const userName = useSelector(getName);
  const userEmail = useSelector(getEmail);

  const { addHost, removeHost, hosts } = useHosts();

  const error = errors[signerId]?.contact?.message ?? '';

  function setContact(value: string) {
    clearErrors(`${signerId}.contact`);
    reset(
      produce(getValues(), (draft) => {
        if (draft[signerId]) {
          draft[signerId].contact = value;
        }
      }),
      { errors: true }
    );
  }

  function resetContact() {
    setContact('');
  }

  function updateDefaultHost(method: SigningMethod | null) {
    const hasDefaultHost = hosts.find((host) => host.contact === userEmail);
    const hasOtherInPersonSigner =
      Object.entries(getValues())
        .filter(([key]) => key !== signerId)
        .filter(([, value]) => value.signingMethod === 'IN_PERSON').length > 0;

    const canRemoveHost = hasDefaultHost && !hasOtherInPersonSigner;

    if (method === 'IN_PERSON' && !hasDefaultHost) {
      addHost({
        type: 'HOST',
        id: v4(),
        dataLabel: createDataLabel(),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        name: userName!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        contact: userEmail!,
        locale: 'ko',
      });
    } else if (canRemoveHost) {
      hosts.forEach((host) => removeHost(host.id));
    }
  }

  function handleChangeSigningMethod(method: SigningMethod) {
    if (type === method) {
      return;
    }

    pushAdobeDataLayer({
      event: 'clickMeanTypeSelector',
      signingMethodType: method,
    });

    if (shouldBlockInPersonSigning(method === 'IN_PERSON')) {
      setIsInPersonUpSellModalShow(true);
      return;
    }

    if (shouldSetUserAsDefaultHost) {
      /**
       * reset 이후에 getValues 하면 빈 react-hook-from 객체를 반환하는 에러가 있음. 때문에 가장 위에서 호출해야함
       * https://github.com/react-hook-form/react-hook-form/issues/2320
       * TODO: v7로 바꾸면서 hook-form 문제 계속 있는지 확인 필요
       */
      updateDefaultHost(method);
    }

    onSigningMethodChange(method);
    setContact(getContact(method, addressBookContact));
  }

  function getBlockedSigningMethods() {
    const types: SigningMethod[] = [];
    if (shouldBlockInPersonSigning(true)) {
      types.push('IN_PERSON');
    }
    return types;
  }

  switch (type) {
    case null:
      return (
        <>
          <SignerCardButtons
            blockedByPermissionTypes={getBlockedSigningMethods()}
            selectableMethods={selectableMethods}
            onSelectMethodClick={(method) => {
              pushAdobeDataLayer({
                event: 'clickMeanTypeButton',
                signingMethodType: method,
              });
              if (shouldBlockInPersonSigning(method === 'IN_PERSON')) {
                setIsInPersonUpSellModalShow(true);
                return;
              }

              if (shouldSetUserAsDefaultHost) {
                /**
                 * reset 이후에 getValues 하면 빈 react-hook-from 객체를 반환하는 에러가 있음. 때문에 가장 위에서 호출해야함
                 * https://github.com/react-hook-form/react-hook-form/issues/2320
                 * TODO: v7로 바꾸면서 hook-form 문제 계속 있는지 확인 필요
                 */
                updateDefaultHost(method);
              }

              onSigningMethodChange(method);
              resetContact();
            }}
          />
          <InPersonUpSellingModal
            isOpen={isInPersonUpSellModalShow}
            onCloseClicked={() => setIsInPersonUpSellModalShow(false)}
          />
        </>
      );
    case 'EMAIL':
      return (
        <Controller
          control={control}
          defaultValue=""
          name={`${signerId}.contact`}
          render={({ value: contact, onChange: onContactChange }) => {
            const debounceTrigger = debounce(trigger, 600);
            return (
              <>
                <Flex>
                  <Box width="80px">
                    <SigningMethodSelect value={'EMAIL'} onSelect={handleChangeSigningMethod} />
                    <InPersonUpSellingModal
                      isOpen={isInPersonUpSellModalShow}
                      onCloseClicked={() => setIsInPersonUpSellModalShow(false)}
                    />
                  </Box>
                  <Box width="100%">
                    <EmailAutoCompleteInput
                      autoFocus
                      domainSuggestions={[
                        { id: 'naver', label: 'naver.com' },
                        { id: 'gmail', label: 'gmail.com' },
                        { id: 'hanmail', label: 'hanmail.net' },
                        { id: 'daum', label: 'daum.net' },
                        { id: 'kakao', label: 'kakao.com' },
                        { id: 'nate', label: 'nate.com' },
                      ]}
                      error={error}
                      initialValue={contact ?? ''}
                      maxLength={CONTACT_INFO_MAX_LENGTH}
                      name="signerEmail"
                      placeholder="이메일 주소"
                      onChange={(v: string) => {
                        // 현재 입력 중인 인풋 외 다른 인풋도 같이 검증하여 contact info의 중복체크를 합니다.
                        debounceTrigger();
                        onContactChange(v);
                      }}
                      onClear={() => {
                        resetContact();
                      }}
                    />
                  </Box>
                </Flex>
                {isLocaleChangeAvailable && <SignerLanguageSelect signerId={signerId} />}
              </>
            );
          }}
          rules={{
            validate: {
              isInvalid: (value) => {
                if (!required && !value) {
                  return true;
                }
                return validateEmail(value).message ? validateEmail(value).message : true;
              },
              isDuplicated: (value) =>
                duplicatedContactInfoValidator({
                  signers: getValues(),
                  signerId,
                  order,
                  targetContact: value,
                  errors,
                }),
            },
          }}
          shouldUnregister
        />
      );
    case 'KAKAO':
      return (
        <>
          <SignerFormPhoneField
            order={order}
            required={required}
            signerId={signerId}
            onContactValueReset={resetContact}
            onSigningMethodChange={handleChangeSigningMethod}
          />
          {isLocaleChangeAvailable && <SignerLanguageSelect signerId={signerId} />}
          <InPersonUpSellingModal
            isOpen={isInPersonUpSellModalShow}
            onCloseClicked={() => setIsInPersonUpSellModalShow(false)}
          />
        </>
      );
    case 'IN_PERSON':
      return (
        <InPersonContactInfoForm
          order={order}
          required={required}
          setUserAsDefaultHost={shouldSetUserAsDefaultHost}
          signerId={signerId}
          onContactReset={resetContact}
          onSingingMethodChange={handleChangeSigningMethod}
        />
      );
    case 'SECURE_LINK':
      return (
        <Controller
          control={control}
          name={`${signerId}.contact`}
          render={({ value: contact, onChange: onContactChange }) => {
            return (
              <Box mb={4}>
                <Box mb={3} position="relative" width="100%">
                  <Box
                    as="input"
                    autoFocus
                    className={cx({
                      'InputSet__text-input': true,
                      'InputSet__text-input--with-clear-button': !!contact,
                    })}
                    name="secure-link-contact"
                    placeholder="이메일 주소 또는 휴대폰 번호"
                    type="text"
                    value={contact}
                    onChange={onContactChange}
                  />
                  {!!contact && (
                    <button
                      aria-label="clear"
                      className="InputSet__clear-button"
                      type="button"
                      onClick={() => {
                        resetContact();
                      }}
                    />
                  )}
                  {error && (
                    <ValidationMessage fontSize="sm" fontWeight="regular">
                      {error}
                    </ValidationMessage>
                  )}
                </Box>
                <Tooltip
                  content={
                    <>
                      서명자의 연락처로 서명 요청 알림을 발송하지 않고,
                      <br /> 요청자가 제공하는 경로를 통해 서명하도록 합니다.
                    </>
                  }
                  placement="right"
                >
                  <Box bg="boxBackgroundNeutralBase" borderRadius="3px" px={5} py={4}>
                    <Text color="textNeutralSlightlyLight" fontSize="md">
                      <FontAwesomeIcon icon={faUserShield} style={{ marginRight: '8px' }} />
                    </Text>
                    <Text color="textNeutralSlightlyLight" fontSize="13px">
                      보안링크
                    </Text>
                    <Text color="textNeutralLight" fontSize="13px">
                      로 참여합니다.
                    </Text>
                  </Box>
                </Tooltip>
              </Box>
            );
          }}
          rules={{
            required: '이메일 주소 또는 휴대폰 번호를 입력해주세요.',
            validate: {
              isInvalid: (value) => {
                const { message } = validatePhoneNumber(value);
                if (!!validateEmail(value).message && !!message) {
                  return '이메일 주소 또는 휴대폰 번호를 입력해주세요.';
                }

                return true;
              },
              isDuplicated: (value) =>
                duplicatedContactInfoValidator({
                  signers: getValues(),
                  signerId,
                  order,
                  targetContact: value,
                  errors,
                }),
            },
          }}
          shouldUnregister
        />
      );
    default:
      return null;
  }
}

SignerFormContactAndMethodField.defaultProps = defaultProps;
